<template>
  <div>
    <el-upload
      :ref="'uploadFile_'+ type"
      action="#"
      :auto-upload="false"
      :limit="limitNum"
      :accept="'.jpg,.jpeg,.png,.pdf'"
      :on-preview="handlePreview.bind(this,type)"
      :on-change="handleFile.bind(this,type)"
      :on-remove="handleRemove.bind(this,type)"
      :before-remove="handleBeforeRemove.bind(this,type)"
      :on-exceed="handleFileExceed.bind(this,type)"
      :file-list="fileList[type]"
      :disabled="disabled"
    >
      <div v-show="!uploading[type]" class="upload-container full-style">
        <div class="upload-inner">
          <div class="icon-add">+</div>
          <div class="upload-main-text">{{ $t('kyc.upload_document') }}</div>
          <div class="upload-sub-text">{{ $t('kyc.upload_tip') }}</div>
          <div class="upload-tip">{{ $t('kyc.upload_format') }}</div>
        </div>
      </div>
    </el-upload>
    <Progress :progress="uploadingProgress[type]" />
    <el-dialog :visible.sync="dialogImageVisible">
      <img width="100%" :src="dialogImageUrl" alt="">
    </el-dialog>
  </div>

</template>
<script>
import { uploadFileWithProgress } from '@/api/components/upload'
import { allowAction } from '@/utils/rateLimit'

export default {
  name: 'UpdateFile',
  components: {
    Progress: () => import('@/views/components/Progress.vue')
  },
  props: {
    limitNum: {
      type: Number,
      default: 5
    },
    disabled: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: ''
    },
    fileList: {
      type: Object,
      default: function() {
        return {}
      }
    },
    uploading: {
      type: Object,
      default: function() {
        return {}
      }
    },
    removeFiles: {
      type: Array,
      default: function() {
        return []
      }
    },
    uploadingProgress: {
      type: Object,
      default: function() {
        return {}
      }
    }
  },
  data() {
    return {
      dialogImageUrl: '',
      dialogImageVisible: false,
      lastUploadAt: {}
    }
  },
  methods: {
    handleFile(type, file) {
      // 检查文件大小，最大10MB
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (file.size > maxSize) {
        this.$message.warning(this.$t('tips.file_size_exceeded') || 'File size cannot exceed 10MB')
        // 移除刚添加的文件
        const idx = this.$refs['uploadFile_' + type].uploadFiles.findIndex(item => item.uid === file.uid)
        if (idx !== -1) {
          this.$refs['uploadFile_' + type].uploadFiles.splice(idx, 1)
        }
        return
      }
      // 检查文件类型是否为允许的格式：JPG, JPEG, PNG, PDF
      const isImage = file.raw.type.startsWith('image/')
      const isPdf = file.raw.type === 'application/pdf'
      const fileExtension = file.name?.toLowerCase().split('.').pop() || ''
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf']
      const isAllowedExtension = allowedExtensions.includes(fileExtension)

      if (!isImage && !isPdf && !isAllowedExtension) {
        this.$message.warning(this.$t('tips.please_upload_image_or_pdf'))
        // 移除刚添加的文件
        const idx = this.$refs['uploadFile_' + type].uploadFiles.findIndex(item => item.uid === file.uid)
        if (idx !== -1) {
          this.$refs['uploadFile_' + type].uploadFiles.splice(idx, 1)
        }
        return
      }
      // per-type rate limit
      if (!allowAction(`upload_${type}`, 800)) {
        this.$message.warning(this.$t ? this.$t('kyc.upload_debounce_warning') || 'Uploading, please wait' : 'Uploading, please wait')
        return
      }
      if (this.uploading[type]) {
        this.$message.warning(this.$t ? this.$t('kyc.upload_debounce_warning') || 'Uploading, please wait' : 'Uploading, please wait')
        return
      }
      this.$emit('changeUploading', { type, value: true })
      const params = {}
      const formData = new FormData()
      formData.append('file', file.raw)
      if (['sec_id_front_img', 'sec_id_back_img', 'sec_specimen_signature'].indexOf(type) !== -1) {
        formData.append('business', 'MERCHANT_EMPLOYEE_INFO')
      } else if (['app_logo'].indexOf(type) !== -1) {
        formData.append('business', 'MERCHANT_APP_INFO')
      } else {
        formData.append('business', 'MERCHANT_INFO')
      }
      params.data = formData
      // 上传进度，固定到大概95%，等后台响应成功显示100%
      params.onUploadProgress = (progressEvent) => {
        const value = Math.floor((progressEvent.loaded * 100) / (progressEvent.total * 1.05))
        this.$emit('changeUploadingProgress', { type, value })
      }
      uploadFileWithProgress(params).then(res => {
        if (res.code === 10000) {
          if (this.removeFiles.includes(file.uid)) {
            return
          }
          this.$message.success('upload successfully')
          const config = {
            push: true,
            file,
            type,
            content_oss_url: res.data.path,
            resData: res.data.path
          }
          this.$emit('changeFileList', config)
          this.$emit('changeUploadingProgress', { type, value: 100 })
          setTimeout(() => {
            this.$emit('changeUploadingProgress', { type, value: 0 })
          }, 500)
        }
      }).catch((err) => {
        // 上传失败，文件还是会显示，要移除文件
        console.error('err', err)
        const uid = file.uid
        const idx = this.$refs['uploadFile_' + type].uploadFiles.findIndex(item => item.uid === uid)
        if (idx !== -1) {
          this.$refs['uploadFile_' + type].uploadFiles.splice(idx, 1)
        }
      }).finally(() => {
        this.$emit('changeUploading', { type, value: false })
        this.$emit('changeUploadingProgress', { type, value: 0 })
      })
    },
    handleRemove(type, file, fileList) {
      const config = {
        type,
        typeFileList: fileList,
        file,
        delete: true
      }
      this.$emit('changeFileList', config)
      if (file.status === 'ready') {
        // 重置进度条
        this.$emit('changeUploadingProgress', { type, value: 0 })
        // 重置上传状态
        this.$emit('changeUploading', { type, value: false })
        // 正在上传中，移除，添加记录，等上传成功之后不进行添加
        this.$emit('changeRemoveFiles', { push: true, file })
      }
    },
    handleBeforeRemove(type, file, fileList) {
      // 移除前
      return this.$confirm(this.$t('kyc.confirm_remove_file', { name: file.name }))
    },
    handleFileExceed(type) {
      if (this.fileList[type].length >= this.limitNum) {
        this.$message.warning(this.$t('kyc.validation_file_limit_exceeded', { limit: this.limitNum }))
        return
      }
    },
    handlePreview(type, file, fileList) {
      this.fileView(file)
    },
    fileView(file) {
      const fileUrl = file.content_oss_url || file.content || ''
      // Extract file extension from URL (before query parameters)
      const urlWithoutParams = fileUrl.split('?')[0]
      const extension = urlWithoutParams.toLowerCase().split('.').pop()

      if (extension === 'pdf') {
        window.open(fileUrl)
        return
      }
      if (['gif', 'jpg', 'jpeg', 'png'].includes(extension)) {
        this.dialogImageVisible = true
        this.dialogImageUrl = fileUrl
        return
      }
      // For other file types, try to open with office apps viewer
      window.open(
        'https://view.officeapps.live.com/op/view.aspx?src=' + fileUrl,
        '_blank'
      )
    }
  }
}
</script>
<style lang="scss" scoped>
.btn-update{
  margin-left: 10px;
}
.upload-container{
    background-color: #fbfdff;
    border: 1px dashed #d1d5db;
    border-radius: 8px;
    box-sizing: border-box;
    width: 100%;
    cursor: pointer;
    vertical-align: top;
    font-size: 14px;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 28px 16px;
    .icon-add{
      font-size: 28px;
      color: #9ca3af;
      margin-bottom: 8px;
    }
    .upload-inner{
      text-align: center;
    }
    .upload-main-text{
      font-size: 16px;
      color: #2563eb;
      font-weight: 500;
      margin-top: 6px;
      margin-bottom: 6px;
    }
    .upload-sub-text{
      font-size: 12px;
      color: #9ca3af;
      margin-bottom: 6px;
    }
    .upload-tip{
      font-size: 12px;
      color: #9ca3af;
    }
}
</style>
